import { Outlet, ScrollRestoration, type RouteObject } from 'react-router';
import { lazy } from 'react';

import { RouteErrorBoundary } from '@/components/route-error-boundary';
import { AuthLayout } from '@/layouts/auth-layout';
import { MarketingLayout } from '@/layouts/marketing-layout';

// Marketing + auth pages are imported eagerly: they're small, prerendered,
// and navigating between them must not flash a Suspense fallback. Only the
// dashboard (the overwhelming majority of the bundle) is code-split.
import HomePage from '@/pages/home-page';
import BookDemoPage from '@/pages/unauthenticated/book-demo/book-demo-page';
import CliErrorPage from '@/pages/unauthenticated/auth/cli/error/cli-error-page';
import CliPage from '@/pages/unauthenticated/auth/cli/cli-page';
import CliSuccessPage from '@/pages/unauthenticated/auth/cli/success/cli-success-page';
import LoginPage from '@/pages/unauthenticated/auth/login/login-page';
import LogoutPage from '@/pages/unauthenticated/auth/logout/logout-page';
import PricingPage from '@/pages/unauthenticated/pricing/pricing-page';
import PrivacyPolicyPage from '@/pages/unauthenticated/policies/privacy-policy/privacy-policy-page';
import RegisterPage from '@/pages/unauthenticated/auth/register/register-page';
import ResetPasswordCompletePage from '@/pages/unauthenticated/auth/reset-password/complete/reset-password-complete-page';
import ResetPasswordPage from '@/pages/unauthenticated/auth/reset-password/reset-password-page';
import ResetPasswordRequestPage from '@/pages/unauthenticated/auth/reset-password/request/reset-password-request-page';
import SupportPage from '@/pages/unauthenticated/support/support-page';
import TermsOfServicePage from '@/pages/unauthenticated/policies/terms-of-service/terms-of-service-page';
import ThankYouPage from '@/pages/unauthenticated/auth/register/thank-you/thank-you-page';
import VerifyAccountPage from '@/pages/unauthenticated/auth/verify/account/verify-account-page';
import VerifyErrorPage from '@/pages/unauthenticated/auth/verify/error/verify-error-page';
import VerifyInvitePage from '@/pages/unauthenticated/auth/verify/invite/verify-invite-page';

const lz = (loader: () => Promise<{ default: React.ComponentType }>) => ({
  Component: lazy(loader),
});

const DashboardLayout = lazy(() =>
  import('@/layouts/dashboard-layout').then((m) => ({
    default: m.DashboardLayout,
  })),
);

export const routes: RouteObject[] = [
  {
    // ScrollRestoration resets scroll to top on navigation (and restores it on
    // back/forward). React Router's data router does NOT do this by default, so
    // without it the scroll position carries over between pages.
    element: (
      <>
        <ScrollRestoration />
        <Outlet />
      </>
    ),
    ErrorBoundary: RouteErrorBoundary,
    children: [
      // Marketing
      {
        Component: MarketingLayout,
        children: [
          { path: '/', Component: HomePage },
          { path: '/pricing', Component: PricingPage },
          { path: '/support', Component: SupportPage },
          { path: '/book-demo', Component: BookDemoPage },
          { path: '/terms-of-service', Component: TermsOfServicePage },
          { path: '/privacy-policy', Component: PrivacyPolicyPage },
        ],
      },

      // Auth (unauthenticated)
      {
        Component: AuthLayout,
        children: [
          { path: '/login', Component: LoginPage },
          { path: '/register', Component: RegisterPage },
          { path: '/register/thank-you', Component: ThankYouPage },
          { path: '/logout', Component: LogoutPage },
          { path: '/verify/account', Component: VerifyAccountPage },
          { path: '/verify/invite', Component: VerifyInvitePage },
          { path: '/verify/error', Component: VerifyErrorPage },
          { path: '/reset-password', Component: ResetPasswordPage },
          { path: '/reset-password/request', Component: ResetPasswordRequestPage },
          { path: '/reset-password/complete', Component: ResetPasswordCompletePage },
          { path: '/cli', Component: CliPage },
          { path: '/cli/success', Component: CliSuccessPage },
          { path: '/cli/error', Component: CliErrorPage },
        ],
      },

      // Dashboard (authenticated)
      {
        Component: DashboardLayout,
        children: [
          {
            path: '/dashboard',
            ...lz(() => import('@/pages/authenticated/dashboard/dashboard-page')),
          },
          {
            path: '/no-context',
            ...lz(() => import('@/pages/authenticated/no-context/no-context-page')),
          },

          // Admin (God-only; the page itself gates on the GOD role)
          {
            path: '/dashboard/admin/view-as',
            ...lz(
              () => import('@/pages/authenticated/dashboard/admin/view-as-page'),
            ),
          },

          // Flags
          {
            path: '/dashboard/flags',
            ...lz(() => import('@/pages/authenticated/dashboard/flags/flags-page')),
          },
          {
            path: '/dashboard/flags/new',
            ...lz(
              () => import('@/pages/authenticated/dashboard/flags/new/new-flag-page'),
            ),
          },
          {
            path: '/dashboard/flags/:id',
            ...lz(
              () =>
                import('@/pages/authenticated/dashboard/flags/detail/flag-detail-layout'),
            ),
            children: [
              {
                index: true,
                ...lz(
                  () =>
                    import(
                      '@/pages/authenticated/dashboard/flags/detail/overview/flag-overview-tab-page'
                    ),
                ),
              },
              {
                // Alias of the index route — keeps the explicit /overview URL
                // working now that Overview is the default tab.
                path: 'overview',
                ...lz(
                  () =>
                    import(
                      '@/pages/authenticated/dashboard/flags/detail/overview/flag-overview-tab-page'
                    ),
                ),
              },
              {
                path: 'targeting',
                ...lz(
                  () =>
                    import('@/pages/authenticated/dashboard/flags/detail/flag-targeting-page'),
                ),
              },
              {
                path: 'variants',
                ...lz(
                  () =>
                    import(
                      '@/pages/authenticated/dashboard/flags/detail/variants/flag-variants-page'
                    ),
                ),
              },
              {
                path: 'insights',
                ...lz(
                  () =>
                    import(
                      '@/pages/authenticated/dashboard/flags/detail/insights/flag-insights-page'
                    ),
                ),
              },
              {
                path: 'live-tail',
                ...lz(
                  () =>
                    import(
                      '@/pages/authenticated/dashboard/flags/detail/live-tail/flag-live-tail-page'
                    ),
                ),
              },
              {
                path: 'change-history',
                ...lz(
                  () =>
                    import(
                      '@/pages/authenticated/dashboard/flags/detail/change-history/flag-change-history-page'
                    ),
                ),
              },
            ],
          },

          // Experiments
          {
            path: '/dashboard/experiments',
            ...lz(
              () => import('@/pages/authenticated/dashboard/experiments/experiments-page'),
            ),
          },
          {
            path: '/dashboard/experiments/new',
            ...lz(
              () =>
                import('@/pages/authenticated/dashboard/experiments/new/new-experiment-page'),
            ),
          },
          {
            // Full-width experiment detail page: identity + meta fold into the
            // header, with Configuration and Results spanning the full width
            // (no right sidebar / tabs).
            path: '/dashboard/experiments/:id',
            ...lz(
              () =>
                import(
                  '@/pages/authenticated/dashboard/experiments/detail/experiment-detail-page'
                ),
            ),
          },

          // Metrics
          {
            path: '/dashboard/metrics',
            ...lz(() => import('@/pages/authenticated/dashboard/metrics/metrics-page')),
          },
          {
            path: '/dashboard/metrics/new',
            ...lz(
              () => import('@/pages/authenticated/dashboard/metrics/new/new-metric-page'),
            ),
          },
          {
            // Full-width metric detail page: identity + meta fold into the
            // header, with live analytics (KPIs, charts) and connected
            // experiments spanning the full width (no right sidebar / tabs).
            path: '/dashboard/metrics/:id',
            ...lz(
              () =>
                import(
                  '@/pages/authenticated/dashboard/metrics/detail/metric-detail-page'
                ),
            ),
          },

          // Events
          {
            path: '/dashboard/events',
            ...lz(() => import('@/pages/authenticated/dashboard/events/events-page')),
          },

          // AI
          {
            path: '/dashboard/ai',
            ...lz(() => import('@/pages/authenticated/dashboard/ai/ai-page')),
          },
          {
            path: '/dashboard/ai/chat/:id',
            ...lz(
              () => import('@/pages/authenticated/dashboard/ai/chat/detail/ai-chat-page'),
            ),
          },

          // Help
          {
            path: '/dashboard/help',
            ...lz(() => import('@/pages/authenticated/dashboard/help/help-page')),
          },
          {
            path: '/dashboard/help/sdk-setup',
            ...lz(
              () =>
                import('@/pages/authenticated/dashboard/help/sdk-setup/sdk-setup-page'),
            ),
          },
          {
            path: '/dashboard/help/events-setup',
            ...lz(
              () =>
                import(
                  '@/pages/authenticated/dashboard/help/events-setup/events-setup-page'
                ),
            ),
          },

          // Settings
          {
            path: '/dashboard/settings',
            ...lz(
              () => import('@/pages/authenticated/dashboard/settings/settings-page'),
            ),
          },
          {
            path: '/dashboard/settings/profile',
            ...lz(
              () =>
                import('@/pages/authenticated/dashboard/settings/profile/profile-layout'),
            ),
            children: [
              {
                index: true,
                ...lz(
                  () =>
                    import(
                      '@/pages/authenticated/dashboard/settings/profile/profile-page'
                    ),
                ),
              },
              {
                path: 'audit',
                ...lz(
                  () =>
                    import(
                      '@/pages/authenticated/dashboard/settings/profile/audit/profile-audit-page'
                    ),
                ),
              },
            ],
          },
          {
            path: '/dashboard/settings/organization',
            ...lz(
              () =>
                import(
                  '@/pages/authenticated/dashboard/settings/organization/organization-page'
                ),
            ),
          },
          {
            path: '/dashboard/settings/organization/billing',
            ...lz(
              () =>
                import(
                  '@/pages/authenticated/dashboard/settings/organization/billing/billing-page'
                ),
            ),
          },
          {
            path: '/dashboard/settings/organization/workspaces',
            ...lz(
              () =>
                import(
                  '@/pages/authenticated/dashboard/settings/organization/workspaces/workspaces-page'
                ),
            ),
          },
          {
            path: '/dashboard/settings/organization/user/:id',
            ...lz(
              () =>
                import(
                  '@/pages/authenticated/dashboard/settings/organization/user/detail/user-detail-page'
                ),
            ),
          },
        ],
      },

      // Unmatched routes — surface as 404 in the boundary
      {
        path: '*',
        loader: () => {
          throw new Response(null, { status: 404, statusText: 'Not Found' });
        },
      },
    ],
  },
];
